import { ref, computed } from 'vue';
import useClientMatterStore from '@/services/useClientMatterStore';
import { useAccountsStore } from '@/services/UserService/useAccountsStore';

/**
 * Composable for managing matter-assigned users in Draft Editor
 * Fetches users assigned to a specific matter and formats them for dropdown display
 */
export function useMatterAssignedUsers() {
  const clientMatterStore = useClientMatterStore();
  const accountsStore = useAccountsStore();
  const assignedUsers = ref([]);
  const loading = ref(false);
  const error = ref(null);

  /**
   * Fetches all active accounts (non-disabled) from the store with caching
   * @returns {Promise<Array>} Array of active account objects
   */
  const fetchAllActiveAccounts = async () => {
    try {
      // Use the store's built-in caching - don't force refresh unless necessary
      const accounts = await accountsStore.listAccounts(false, {
        status: 'active'
      });

      // The 'active' status should already filter out disabled accounts,
      // but let's add an extra safety check
      const activeAccounts = accounts.filter(account => {
        if (account.disabled) {
          return false;
        }
        return true;
      });
      return activeAccounts;
    } catch (err) {
      return [];
    }
  };

  /**
   * Extracts clean user name for display
   * @param {Object} user - User object
   * @returns {string} Clean user name
   */
  const extractUserName = user => {
    // Handle string name
    if (typeof user.name === 'string') {
      return user.name;
    }

    // Handle nested name object
    if (user.name?.name) {
      return user.name.name;
    }

    // Handle firstName/lastName combination
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }

    // Handle single name properties
    if (user.firstName) {
      return user.firstName;
    }
    if (user.lastName) {
      return user.lastName;
    }

    // Return undefined if no name found
    return undefined;
  };

  /**
   * Fetches users assigned to a matter and filters out disabled users
   * @param {string} matterId - The matter ID to fetch users for
   * @returns {Promise<Array>} Array of active user objects with name and id
   */
  const fetchMatterAssignedUsers = async matterId => {
    if (!matterId) {
      assignedUsers.value = [];
      return [];
    }
    loading.value = true;
    error.value = null;
    try {
      // First, fetch all active (non-disabled) accounts for filtering
      const activeAccounts = await fetchAllActiveAccounts();
      const activeAccountIds = new Set(activeAccounts.map(account => account.id));
      const matter = await clientMatterStore.getMatterById(matterId);
      let users = matter?.assignedUsers || matter?.users;
      if (!users || !Array.isArray(users)) {
        console.warn('No assigned users found for matter:', matterId);
        assignedUsers.value = [];
        return [];
      }

      // Filter out disabled users by comparing with active accounts list
      const activeUsers = users.filter(user => {
        const isActive = activeAccountIds.has(user.id);
        return isActive;
      });

      // Format users for display
      const formattedUsers = activeUsers.map(user => {
        const userName = extractUserName(user) || `User ${user.id}` || 'Unknown User';
        return {
          id: user.id,
          name: userName,
          displayName: userName,
          // Preserve initials for description generation
          initials: user.initials,
          // Preserve other potentially useful properties
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email
        };
      });
      assignedUsers.value = formattedUsers;
      return formattedUsers;
    } catch (err) {
      console.error('Error fetching matter assigned users:', err);
      error.value = 'Failed to fetch assigned users for this matter';
      assignedUsers.value = [];
      return [];
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fallback to get all active accounts when matter-based filtering is not enabled
   * Also filters out disabled users by comparing against the active accounts list
   * @param {Array} groups - Optional groups containing lineItems with accounts to merge
   */
  const fetchAllAccounts = async (groups = []) => {
    loading.value = true;
    error.value = null;
    try {
      // Fetch only active (non-disabled) accounts (uses store caching)
      const activeAccounts = await fetchAllActiveAccounts();
      const formattedAccounts = activeAccounts.map(account => {
        const accountName = extractUserName(account) || `Account ${account.id}` || 'Unknown Account';
        return {
          id: account.id,
          name: accountName,
          displayName: accountName,
          // Preserve initials for description generation
          initials: account.initials,
          // Preserve other potentially useful properties
          firstName: account.firstName,
          lastName: account.lastName,
          email: account.email
        };
      });

      // Merge accounts from lineItems if groups are provided (also filter disabled)
      const allAccounts = [...formattedAccounts];
      if (groups && Array.isArray(groups)) {
        const activeAccountIds = new Set(activeAccounts.map(account => account.id));
        groups.forEach(group => {
          if (group.lineItems && Array.isArray(group.lineItems)) {
            group.lineItems.forEach(li => {
              if (!li.account) return;
              // Only include if account is active and not already in the list
              if (activeAccountIds.has(li.account.id) && !allAccounts.find(a => a.id === li.account.id)) {
                // Get the original account data from activeAccounts to ensure clean name
                const originalAccount = activeAccounts.find(acc => acc.id === li.account.id);
                if (originalAccount) {
                  const liAccountName = extractUserName(originalAccount) || `Account ${originalAccount.id}` || 'Unknown Account';
                  allAccounts.push({
                    id: originalAccount.id,
                    name: liAccountName,
                    displayName: liAccountName,
                    // Preserve initials for description generation from original account
                    initials: originalAccount.initials,
                    // Preserve other potentially useful properties from original account
                    firstName: originalAccount.firstName,
                    lastName: originalAccount.lastName,
                    email: originalAccount.email
                  });
                }
              }
            });
          }
        });
      }
      assignedUsers.value = allAccounts;
      return allAccounts;
    } catch (err) {
      console.error('Error fetching all accounts:', err);
      error.value = 'Failed to fetch user accounts';
      assignedUsers.value = [];
      return [];
    } finally {
      loading.value = false;
    }
  };

  /**
   * Forces a refresh of the accounts cache (useful after user management changes)
   */
  const refreshAccountsCache = async () => {
    // Force refresh the store cache
    await accountsStore.listAccounts(true, {
      status: 'active'
    });
  };

  // Computed property to check if there are any assigned users
  const hasAssignedUsers = computed(() => assignedUsers.value.length > 0);

  // Computed property to get empty state message
  const emptyStateMessage = computed(() => {
    if (loading.value) return 'Loading users...';
    if (error.value) return error.value;
    return 'No users assigned to this matter';
  });
  return {
    assignedUsers,
    loading,
    error,
    hasAssignedUsers,
    emptyStateMessage,
    fetchMatterAssignedUsers,
    fetchAllAccounts,
    fetchAllActiveAccounts,
    refreshAccountsCache
  };
}